import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { useResizeObserver } from './useResizeObserver';

const resizeObserverOptions = {};

const RESERVED_SPACE_PROPERTY = '--results-reserved-space';

const calculateHeight = (
  containerElement: HTMLElement,
  minHeight = 0,
  reservedSpace = 0,
) => {
  const viewportHeight = window.innerHeight;

  const containerTop = containerElement.getBoundingClientRect().top;

  const layoutReservedSpace =
    Number.parseFloat(
      getComputedStyle(containerElement).getPropertyValue(
        RESERVED_SPACE_PROPERTY,
      ),
    ) || 0;

  const availableHeight =
    viewportHeight - containerTop - reservedSpace - layoutReservedSpace;
  const calculatedHeight = Math.max(minHeight, availableHeight);

  return calculatedHeight;
};

/**
 * Custom hook to calculate and maintain the optimal height for a results container.
 * It dynamically adjusts the height based on the viewport size, ensuring it fills
 * the available space while respecting a specified minimum height.
 *
 * A layout reserves space below every results container at once by setting
 * the inherited `--results-reserved-space` custom property in px, e.g. for the
 * bottom padding of its scrolling content area.
 *
 * @param minHeight - The minimum height in px the container should have.
 * @param reservedSpace - Space in px to reserve at the bottom of the viewport,
 *   in addition to `--results-reserved-space`.
 * @returns An object containing the current results height and a ref for the results wrapper.
 */
export const useOptimalResultsHeight = (minHeight = 96, reservedSpace = 0) => {
  const resultsWrapperRef = useRef<HTMLDivElement>(null);

  const [resultsHeight, setResultsHeight] = useState(minHeight);

  const handleResize = useCallback<ResizeObserverCallback>(
    (entries) => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      setResultsHeight(
        calculateHeight(entry.target as HTMLElement, minHeight, reservedSpace),
      );
    },
    [minHeight, reservedSpace],
  );

  // Use window.resize listener to catch vertical-only resizing
  useLayoutEffect(() => {
    const handleWindowResize = () => {
      if (!resultsWrapperRef.current) {
        return;
      }

      // oxlint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect -- intentional: measure DOM after layout to size the container; cannot be derived during render because the ref is null on first commit
      setResultsHeight(
        calculateHeight(resultsWrapperRef.current, minHeight, reservedSpace),
      );
    };

    // Calculate initial height
    handleWindowResize();

    window.addEventListener('resize', handleWindowResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [minHeight, reservedSpace]);

  // Use ResizeObserver for other layout changes on the container itself
  useResizeObserver(
    resultsWrapperRef.current ?? undefined,
    resizeObserverOptions,
    handleResize,
  );

  // Observe parent element to detect when elements above the container change size
  // This catches cases where the container's position changes due to sibling elements
  useLayoutEffect(() => {
    if (!resultsWrapperRef.current?.parentElement) {
      return;
    }

    const parentElement = resultsWrapperRef.current.parentElement;

    const parentResizeObserver = new ResizeObserver(() => {
      if (!resultsWrapperRef.current) {
        return;
      }

      setResultsHeight(
        calculateHeight(resultsWrapperRef.current, minHeight, reservedSpace),
      );
    });

    parentResizeObserver.observe(parentElement);

    return () => {
      parentResizeObserver.disconnect();
    };
  }, [minHeight, reservedSpace]);

  return {
    resultsHeight,
    resultsWrapperRef,
  };
};
